import { memo } from 'react';
import PropTypes from 'prop-types';
import {
	isPipelineBuiltinChartKind,
	resolveCompositionChartConfigFromSnapshot,
	resolveOverdueChartConfigFromSnapshot,
	resolvePersistedChartKind,
	PERSISTED_CHART_KIND,
} from '../lib/statisticsPersistedCharts';
import { rendersAsBuiltinLiveChart, rendersAsFrozenBuiltinChart } from '../lib/dashboardChartLifecycle';
import TrendChart from '../../trend/TrendChart';
import CompositionChart from '../../composition/CompositionChart';
import OverdueChart from '../../overdue/OverdueChart';
import BuiltinLiveChartBody from './BuiltinLiveChartBody';

const DashboardChartView = ({
	snapshot,
	chartBundle,
	height = '5rem',
	previewHeight = null,
	showPointLabels = false,
	fillHeight = false,
	fillPreview = false,
	className = '',
	enableOrderDrill = false,
	onDrillSelect,
	preferProvidedBundle = false,
}) => {
	if (!snapshot) return null;

	const chartKind = resolvePersistedChartKind(snapshot);
	const resolvedBundle = chartBundle ?? {
		chartLoading: true,
		trendChartLoading: true,
		orders: [],
		compares: [],
		overdueData: {},
		...(chartKind === PERSISTED_CHART_KIND.OVERDUE ? { overdueConfig: resolveOverdueChartConfigFromSnapshot(snapshot) } : {}),
		...(chartKind === PERSISTED_CHART_KIND.COMPOSITION ? { chartConfig: resolveCompositionChartConfigFromSnapshot(snapshot) } : {}),
		animationKey: 'loading',
	};

	const shellFillMode = fillHeight && previewHeight == null;
	const viewClassName = ['statistics-persisted-chart-view', fillHeight ? 'statistics-persisted-chart-view--fill' : null, className].filter(Boolean).join(' ');
	const effectiveFillPreview = fillPreview || shellFillMode;
	const childPreviewHeight = shellFillMode ? null : (previewHeight ?? (fillHeight ? height : null));

	const useBuiltinBody =
		rendersAsBuiltinLiveChart(snapshot) || rendersAsFrozenBuiltinChart(snapshot) || isPipelineBuiltinChartKind(chartKind);
	if (useBuiltinBody) {
		return (
			<div className={viewClassName}>
				<BuiltinLiveChartBody
					snapshot={snapshot}
					height={height}
					fillSlot={fillHeight}
					enableOrderDrill={enableOrderDrill}
					onDrillSelect={onDrillSelect}
					frozenBundle={rendersAsBuiltinLiveChart(snapshot) && !preferProvidedBundle ? null : resolvedBundle}
				/>
			</div>
		);
	}

	if (chartKind === PERSISTED_CHART_KIND.OVERDUE) {
		return (
			<div className={viewClassName}>
				<OverdueChart
					overdueData={resolvedBundle.overdueData}
					overdueConfig={resolvedBundle.overdueConfig}
					isLoading={Boolean(resolvedBundle.chartLoading || resolvedBundle.isStatisticsQueryLoading)}
					height={height}
					previewHeight={childPreviewHeight}
					fillHeight={fillHeight}
					compact
					animationKey={resolvedBundle.animationKey}
					enableOrderDrill={enableOrderDrill}
					onDrillSelect={onDrillSelect}
				/>
			</div>
		);
	}

	if (chartKind === PERSISTED_CHART_KIND.COMPOSITION) {
		return (
			<div className={viewClassName}>
				<CompositionChart
					orders={resolvedBundle.orders}
					primaryPhaseStats={resolvedBundle.primaryPhaseStats}
					chartConfig={resolvedBundle.chartConfig}
					compareSeriesMatrix={resolvedBundle.compareSeriesMatrix}
					selectedPhase={snapshot.selectedPhase ?? null}
					isLoading={Boolean(resolvedBundle.chartLoading || resolvedBundle.isStatisticsQueryLoading)}
					animationKey={resolvedBundle.animationKey}
					height={height}
					previewHeight={childPreviewHeight}
					fillPreview={effectiveFillPreview}
					enableOrderDrill={enableOrderDrill}
					onDrillSelect={onDrillSelect}
				/>
			</div>
		);
	}

	return (
		<div className={viewClassName}>
			<TrendChart
				orders={resolvedBundle.orders}
				highlightProvinceNames={resolvedBundle.highlightProvinceNames}
				highlightAnchorCityRows={resolvedBundle.highlightAnchorCityRows}
				compares={resolvedBundle.compares}
				trendYearMonthsMode={resolvedBundle.trendYearMonthsMode}
				trendDualFullPeriod={resolvedBundle.trendDualFullPeriod}
				trendGranularity={resolvedBundle.trendGranularity}
				trendMonthIndices={resolvedBundle.trendMonthIndices}
				trendPeriodIndices={resolvedBundle.trendPeriodIndices}
				trendPeriodYoy={resolvedBundle.trendPeriodYoy}
				trendDimensionCountSeries={resolvedBundle.trendDimensionCountSeries}
				trendDimensionTotalPriceSeries={resolvedBundle.trendDimensionTotalPriceSeries}
				trendDimensionSeriesByMetric={resolvedBundle.trendDimensionSeriesByMetric}
				trendDimensionFieldLabel={resolvedBundle.trendDimensionFieldLabel}
				trendAxisMode={resolvedBundle.trendAxisMode}
				trendDateField={resolvedBundle.trendDateField}
				trendYAxisMetric={resolvedBundle.trendYAxisMetric}
				trendYAxisMetrics={resolvedBundle.trendYAxisMetrics}
				trendChartType={resolvedBundle.trendChartType}
				trendLineShape={resolvedBundle.trendLineShape}
				trendBarOrientation={resolvedBundle.trendBarOrientation}
				trendMetricSeriesTypes={resolvedBundle.trendMetricSeriesTypes}
				trendYearSeriesColors={resolvedBundle.trendYearSeriesColors}
				selectedPhase={snapshot.selectedPhase ?? null}
				isLoading={Boolean(
					resolvedBundle.trendChartLoading || resolvedBundle.chartLoading || resolvedBundle.isStatisticsQueryLoading,
				)}
				animationKey={resolvedBundle.animationKey}
				height={height}
				previewHeight={childPreviewHeight}
				showPointLabels={showPointLabels}
				fillPreview={effectiveFillPreview}
				enableOrderDrill={enableOrderDrill}
				onDrillSelect={onDrillSelect}
			/>
		</div>
	);
};

DashboardChartView.propTypes = {
	snapshot: PropTypes.object,
	chartBundle: PropTypes.object,
	height: PropTypes.string,
	previewHeight: PropTypes.string,
	showPointLabels: PropTypes.bool,
	fillHeight: PropTypes.bool,
	fillPreview: PropTypes.bool,
	className: PropTypes.string,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
	preferProvidedBundle: PropTypes.bool,
};

export default memo(DashboardChartView);
